import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { filterCss } from "@/lib/mediaEffects";
import { X, MoreVertical, VolumeX, Flag, EyeOff } from "lucide-react";

/**
 * StoryViewerPage (iter88.4 · Multi-Slide + Press-Hold-Pause + Progress-Jump)
 * - Progress-Bars pro Slide oben (soviele wie Slides).
 * - Bild-Slide: 5s Auto-Advance · Video-Slide: läuft bis Ende.
 * - **Press & Hold** (Maus/Touch): Story pausiert (Bild-RAF + Video). Loslassen = weiter.
 * - **Klick auf Progress-Segment**: springt an diesen Slide zurück/vor.
 * - Tap (kurzer Klick) links = Slide zurück, rechts = Slide weiter.
 * - Per-Slide-Caption als Overlay.
 */
const IMAGE_DUR_MS = 5000;
const STORY_REACTIONS = ["❤️", "😍", "🔥", "👏", "😮", "😂"];
const LONG_PRESS_MS = 200;

function formatAge(createdAtIso) {
  if (!createdAtIso) return "";
  const now = Date.now();
  const t = new Date(createdAtIso).getTime();
  const diffMs = Math.max(0, now - t);
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "gerade eben";
  if (min < 60) return `${min} Min.`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} Std.`;
  return "24 Std.";
}

function formatTimeLeft(expiresAtIso) {
  if (!expiresAtIso) return "";
  const now = Date.now();
  const t = new Date(expiresAtIso).getTime();
  const diffMs = Math.max(0, t - now);
  if (diffMs <= 0) return "Läuft ab";
  const min = Math.floor(diffMs / 60000);
  if (min < 60) return `noch ${min} Min.`;
  const hr = Math.floor(min / 60);
  const restMin = min % 60;
  return restMin > 0 ? `noch ${hr}h ${restMin}m` : `noch ${hr}h`;
}

export default function StoryViewerPage() {
  const { t } = useTranslation();
  const { profileId } = useParams();
  const navigate = useNavigate();
  const _area = new URLSearchParams(window.location.search).get("area");
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const pressTimerRef = useRef(null);
  const pressStartRef = useRef({ at: 0, x: 0, rect: null });
  const wasLongPressRef = useRef(false);
  const touchHandledUntilRef = useRef(0);   // Suppress mouse-events fired synth. nach touch
  // Für Image-Timing: elapsed-Millisekunden über Pausen hinweg addieren.
  const elapsedRef = useRef(0);
  const lastTsRef = useRef(0);

  const [story, setStory] = useState(null);
  const [author, setAuthor] = useState({});
  const [idx, setIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [reacting, setReacting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/stories/by-profile/${profileId}${_area === "STX" ? "?area=STX" : ""}`);
        setStory(data.story || null);
        setAuthor(data.author || {});
      } catch (e) {
        if (e?.response?.status === 403) {
          setError(t("story_viewer.avs_required", { defaultValue: "Altersverifikation erforderlich, um stilltouchedX-Storys zu sehen." }));
        } else {
          setError(t("story_viewer.none", { defaultValue: "Keine aktive Story" }));
        }
      }
    })();
  }, [profileId]);

  const slides = story?.slides || [];
  const currentSlide = slides[idx];

  const advance = useCallback(() => {
    if (idx + 1 < slides.length) setIdx(idx + 1);
    else navigate(-1);
  }, [idx, slides.length, navigate]);

  // Slide-Wechsel: alles zurücksetzen
  useEffect(() => {
    setProgress(0);
    elapsedRef.current = 0;
    lastTsRef.current = 0;
  }, [idx]);

  // iter322 · Zuschauer-Tracking: pro Element genau einmal pro Session melden (nur Social).
  const viewedRef = useRef(new Set());
  useEffect(() => {
    const sid = story?.story_id;
    const mu = currentSlide?.media_url;
    if (!sid || !mu || _area === "STX") return;
    const key = `${sid}::${mu}`;
    if (viewedRef.current.has(key)) return;
    viewedRef.current.add(key);
    api.post(`/studio/social/stories/${sid}/view`, { media_url: mu }).catch(() => {});
  }, [story?.story_id, currentSlide?.media_url, _area]);

  // Auto-advance-Loop (Bilder) + Pause-Handling
  useEffect(() => {
    if (!currentSlide) return;
    const isVideo = currentSlide.media_type === "VIDEO";

    // Video: pause/resume das <video>-Element (play() gibt Promise zurück → catch!)
    if (isVideo) {
      const v = videoRef.current;
      if (v) {
        if (paused) {
          try { v.pause(); } catch (_) {}
        } else {
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => {}); // AbortError bei schnellem unmount ignorieren
        }
      }
      return;
    }

    // Bild: RAF-Loop mit Pause-tauglicher Zeitmessung
    if (paused) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    lastTsRef.current = performance.now();
    const tick = (ts) => {
      const dt = ts - (lastTsRef.current || ts);
      lastTsRef.current = ts;
      elapsedRef.current += dt;
      const p = Math.min(100, (elapsedRef.current / IMAGE_DUR_MS) * 100);
      setProgress(p);
      if (p >= 100) { advance(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [idx, currentSlide, paused, advance]);

  // Phase 3 · Hintergrundmusik — pro Slide auflösbar (Slide-Musik > Story-Musik).
  // "Ein Track für alle": story.music bleibt konstant → Audio läuft durch.
  // "Pro Bild": slide.music wechselt → Audio wird beim Slide-Wechsel ausgetauscht.
  const activeMusicUrl = currentSlide?.music?.preview_url || story?.music?.preview_url || "";
  const musicRef = useRef(null);
  const musicUrlRef = useRef("");
  useEffect(() => {
    if (!activeMusicUrl) {
      if (musicRef.current) { try { musicRef.current.pause(); } catch (_) {} musicRef.current = null; musicUrlRef.current = ""; }
      return;
    }
    if (musicUrlRef.current !== activeMusicUrl) {
      if (musicRef.current) { try { musicRef.current.pause(); } catch (_) {} }
      const a = new Audio(activeMusicUrl);
      a.loop = true; a.volume = 0.7;
      musicRef.current = a;
      musicUrlRef.current = activeMusicUrl;
    }
    if (paused) musicRef.current.pause();
    else musicRef.current.play().catch(() => {});
  }, [activeMusicUrl, paused]);
  useEffect(() => () => { if (musicRef.current) { musicRef.current.pause(); musicRef.current = null; } }, []);


  const onVideoTimeUpdate = (e) => {
    const v = e.target;
    if (v.duration > 0) setProgress((v.currentTime / v.duration) * 100);
  };

  // ─── Press-Hold + Tap-Detection ─────────────────────────────
  const handlePressStart = (clientX, targetRect) => {
    wasLongPressRef.current = false;
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressStartRef.current = { at: Date.now(), x: clientX, rect: targetRect };
    pressTimerRef.current = setTimeout(() => {
      wasLongPressRef.current = true;
      setPaused(true);
    }, LONG_PRESS_MS);
  };

  const handlePressEnd = () => {
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
    const wasLong = wasLongPressRef.current;
    wasLongPressRef.current = false;
    if (wasLong) {
      setPaused(false);
      return;
    }
    // Kurzer Tap → links = zurück · rechte 2/3 = weiter
    const p = pressStartRef.current;
    if (p && p.rect) {
      const x = p.x - p.rect.left;
      if (x < p.rect.width / 3) {
        setIdx((cur) => cur > 0 ? cur - 1 : cur);
        setProgress(0);
      } else {
        // GUARD: nur EINE Slide vorwärts (nie doppelt durch synth. mouse-events)
        setIdx((cur) => {
          if (cur + 1 < slides.length) return cur + 1;
          // Letzter Slide → schließen
          setTimeout(() => navigate(-1), 0);
          return cur;
        });
      }
    }
  };

  const handlePressCancel = () => {
    if (pressTimerRef.current) { clearTimeout(pressTimerRef.current); pressTimerRef.current = null; }
    if (wasLongPressRef.current) setPaused(false);
    wasLongPressRef.current = false;
  };

  // Touch (Mobile) — merkt sich Zeitstempel, damit synth. Maus-Events (nach touch) unterdrückt werden
  const onTouchStart = (e) => {
    const t = e.touches[0];
    if (!t) return;
    const rect = e.currentTarget.getBoundingClientRect();
    touchHandledUntilRef.current = Date.now() + 600; // 600ms window to suppress synth mouse
    handlePressStart(t.clientX, rect);
  };
  const onTouchEnd = (e) => {
    // synth. mouseup unterdrücken
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    touchHandledUntilRef.current = Date.now() + 600;
    handlePressEnd();
  };
  const onTouchCancel = () => {
    touchHandledUntilRef.current = Date.now() + 600;
    handlePressCancel();
  };

  // Maus (Desktop) — nur wenn nicht gerade ein Touch verarbeitet wurde
  const onMouseDown = (e) => {
    if (Date.now() < touchHandledUntilRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    handlePressStart(e.clientX, rect);
  };
  const onMouseUp = () => {
    if (Date.now() < touchHandledUntilRef.current) return;
    handlePressEnd();
  };
  const onMouseLeave = () => {
    if (Date.now() < touchHandledUntilRef.current) return;
    handlePressCancel();
  };

  // Progress-Segment-Klick → direkter Sprung
  const jumpToSlide = (i, e) => {
    e.stopPropagation();
    if (i === idx) {
      // Klick auf aktiven Balken: von vorn starten
      setProgress(0);
      elapsedRef.current = 0;
      lastTsRef.current = 0;
      const v = videoRef.current;
      if (v) {
        try { v.currentTime = 0; } catch (_) {}
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
      return;
    }
    if (i >= 0 && i < slides.length) setIdx(i);
  };

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center text-white text-sm">
        {error}
        <button data-testid="story-viewer-close" onClick={() => navigate(-1)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 flex items-center justify-center">
          <X size={18} />
        </button>
      </div>
    );
  }
  if (!story || !currentSlide) {
    return <div className="fixed inset-0 z-50 bg-black flex items-center justify-center text-white text-sm">{t("stories_extra.loading")}</div>;
  }

  const isVideo = currentSlide.media_type === "VIDEO";
  // Per-Slide-Effekte (Fallback auf Story-Ebene für Legacy-Stories)
  const _slideEffects = currentSlide?.effects || story?.effects || {};
  const _fxFilter = _slideEffects.filter && _slideEffects.filter !== "none" ? { filter: filterCss(_slideEffects.filter) } : undefined;

  // iter320 · ⋯-Menü (fremde Story): Autor stummschalten · nicht mehr anzeigen · melden.
  const myId = user && user !== false ? (user._id || user.id) : null;
  const isOwn = !!(myId && story?.author_id && story.author_id === myId);
  const openMenu = () => { setPaused(true); setMenuOpen(true); };
  const closeMenu = () => { setMenuOpen(false); setPaused(false); };
  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 1800); };
  const muteAuthor = () => {
    try {
      const muted = JSON.parse(localStorage.getItem("muted_authors") || "[]");
      if (profileId && !muted.includes(profileId)) { muted.push(profileId); localStorage.setItem("muted_authors", JSON.stringify(muted)); }
    } catch (_) { /* ignore */ }
    window.dispatchEvent(new CustomEvent("feed-refresh"));
    navigate(-1);
  };
  const hideStory = () => {
    try {
      const hidden = JSON.parse(localStorage.getItem("hidden_stories") || "[]");
      if (story?.story_id && !hidden.includes(story.story_id)) { hidden.push(story.story_id); localStorage.setItem("hidden_stories", JSON.stringify(hidden)); }
    } catch (_) { /* ignore */ }
    navigate(-1);
  };
  const reportStory = async () => {
    try { await api.post("/reports", { target_type: "STORY", target_id: story?.story_id, reason: "OTHER" }); } catch (_) { /* ignore */ }
    flash(t("stories_extra.report_done", { defaultValue: "Danke, die Story wurde gemeldet." }));
    closeMenu();
  };
  const sendReaction = async (emoji) => {
    if (reacting || !story?.story_id) return;
    setReacting(true);
    try {
      await api.post(`/studio/social/stories/${story.story_id}/react`, { emoji, media_url: currentSlide?.media_url });
      flash(t("stories_extra.reaction_sent", { defaultValue: "Reaktion gesendet" }));
    } catch (_) {
      flash(t("stories_extra.reaction_failed", { defaultValue: "Reaktion konnte nicht gesendet werden" }));
    } finally { setReacting(false); }
  };

  return (
    <div data-testid="story-viewer" className="fixed inset-0 z-50 bg-black flex items-center justify-center select-none">
      {/* Progress-Bars — klickbar für Sprung */}
      <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 z-30">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            data-testid={`story-progress-${i}`}
            onClick={(e) => jumpToSlide(i, e)}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className="flex-1 h-1.5 bg-white/30 cursor-pointer p-0 border-0"
            aria-label={`Zu Slide ${i + 1} springen`}
          >
            <div
              className="h-full bg-white transition-[width] duration-100 ease-linear"
              style={{ width: `${i < idx ? 100 : i === idx ? progress : 0}%` }}
            />
          </button>
        ))}
      </div>

      {/* Header: Avatar + Name + Timestamp */}
      <div className="absolute top-6 left-0 right-0 flex items-center gap-2 px-3 z-30 pt-2 pointer-events-none">
        <div className="w-8 h-8 rounded-full bg-[#333] overflow-hidden shrink-0">
          {author.avatar_url ? (
            <img src={author.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs text-white/80">
              {(author.display_name || "?")[0]}
            </div>
          )}
        </div>
        <span data-testid="story-viewer-author" className="text-white text-sm font-semibold truncate max-w-[45%]">
          {author.display_name || author.handle || "Creator"}
        </span>
        <span data-testid="story-viewer-timestamp" className="text-white/70 text-xs">
          {formatAge(currentSlide.created_at || story.created_at)}
        </span>
        <div className="flex-1" />
        {!isOwn && (
          <div className="relative pointer-events-auto">
            <button data-testid="story-viewer-more-btn" onClick={() => (menuOpen ? closeMenu() : openMenu())}
              aria-label={t("stories_extra.more", { defaultValue: "Mehr" })} className="p-1 text-white/80 hover:text-white">
              <MoreVertical size={18} />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={closeMenu} />
                <div data-testid="story-viewer-menu" className="absolute right-0 top-9 z-50 min-w-[190px] bg-white rounded-xl shadow-xl py-1">
                  <button data-testid="story-viewer-mute" onClick={muteAuthor}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5]">
                    <VolumeX size={15} className="text-[#999]" /> {t("stories_extra.mute_author", { defaultValue: "Autor stummschalten" })}
                  </button>
                  <button data-testid="story-viewer-hide" onClick={hideStory}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#0A0A0A] hover:bg-[#F5F5F5]">
                    <EyeOff size={15} className="text-[#999]" /> {t("stories_extra.hide_story", { defaultValue: "Nicht mehr anzeigen" })}
                  </button>
                  <button data-testid="story-viewer-report" onClick={reportStory}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-500 hover:bg-red-50">
                    <Flag size={15} /> {t("stories_extra.report_story", { defaultValue: "Story melden" })}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        <button data-testid="story-viewer-close" onClick={() => navigate(-1)} className="p-1 text-white/90 pointer-events-auto">
          <X size={20} />
        </button>
      </div>
      {toast && (
        <div data-testid="story-viewer-toast" className="absolute top-20 left-1/2 -translate-x-1/2 z-50 bg-black/80 text-white text-xs px-4 py-2 rounded-full">
          {toast}
        </div>
      )}

      {/* Media (press-hold pause · tap navigate) */}
      <div
        data-testid="story-viewer-media-area"
        className="w-full h-full flex items-center justify-center cursor-pointer"
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchCancel}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="relative inline-block">
        {isVideo ? (
          <video
            ref={videoRef}
            key={currentSlide.media_url}
            src={currentSlide.media_url}
            autoPlay
            muted
            playsInline
            onTimeUpdate={onVideoTimeUpdate}
            onEnded={advance}
            style={_fxFilter}
            className="block max-w-[100vw] max-h-[100vh] pointer-events-none"
          />
        ) : (
          <img
            key={currentSlide.media_url}
            src={currentSlide.media_url}
            alt=""
            style={_fxFilter}
            className="block max-w-[100vw] max-h-[100vh] object-contain pointer-events-none"
            draggable={false}
          />
        )}
        {/* Phase 3 · Sticker-Overlays (lizenzfrei, pro Slide, relativ zum gerenderten Medium) */}
        {(_slideEffects.stickers || []).map((st, si) => (
          <div key={st.id || `${si}-${st.x}-${st.y}-${st.emoji}`} data-testid="story-viewer-sticker"
            className="absolute z-20 leading-none pointer-events-none select-none"
            style={{ left: `${(st.x ?? 0.5) * 100}%`, top: `${(st.y ?? 0.5) * 100}%`, transform: "translate(-50%,-50%)",
              fontSize: `${st.size || 56}px`, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))" }}>
            {st.emoji}
          </div>
        ))}
        </div>
      </div>


      {/* Paused-Hinweis (subtil) */}
      {paused && (
        <div data-testid="story-viewer-paused" className="absolute top-8 left-1/2 -translate-x-1/2 z-30 px-2 py-0.5 text-[10px] text-white/70 bg-black/40 rounded-full pointer-events-none">
          pausiert
        </div>
      )}

      {/* iter322 · Schnell-Emoji-Reaktionen (nur fremde Social-Storys) → DM an Autor */}
      {!isOwn && _area !== "STX" && (
        <div data-testid="story-reaction-bar"
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-black/40 backdrop-blur-md rounded-full px-3 py-2 pointer-events-auto">
          {STORY_REACTIONS.map((emoji) => (
            <button key={emoji} data-testid={`story-react-${emoji}`} disabled={reacting}
              onClick={(e) => { e.stopPropagation(); sendReaction(emoji); }}
              className="text-2xl leading-none active:scale-125 transition-transform disabled:opacity-50">
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Caption Overlay — Per-Slide Priority, dann Story-Global (Legacy) */}
      {(currentSlide?.caption || story.caption) && (
        <div data-testid="story-viewer-caption" className="absolute bottom-16 left-4 right-4 z-20 text-white text-sm text-center bg-gradient-to-t from-black/80 to-transparent py-4 pointer-events-none">
          {currentSlide?.caption || story.caption}
        </div>
      )}
    </div>
  );
}
